// File: src/pages/ConversationsPage.jsx
// Description: Conversations module with voice/typed capture, ASR via Web Speech API,
// options panel, AI processing, and results (summary, key points, action items,
// follow-ups, translations). Mobile-friendly UI.

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  TextField,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import SendIcon from '@mui/icons-material/Send';
import TranslateIcon from '@mui/icons-material/Translate';
import ChecklistIcon from '@mui/icons-material/Checklist';
import InsightsIcon from '@mui/icons-material/Insights';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

import AIService from '../services/AIService';
import { AuthContext } from '../context/AuthContext';
import { createAuthenticatedAPI } from '../utils/apiClient';

const STORAGE_KEY = 'touchconnect.conversations.draft.v1';

const languageOptions = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' }
];

const initialOptions = {
  summarize: true,
  extractActions: true,
  generateFollowups: true,
  analyzeSentiment: true,
  inputAutoDetect: true,
  outputLanguages: ['en']
};

const ConversationsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useContext(AuthContext);
  const api = useMemo(() => createAuthenticatedAPI({ user, logout }), [user, logout]);

  // States: idle | recording | paused | reviewing | processing | results
  const [state, setState] = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [partial, setPartial] = useState('');
  const [options, setOptions] = useState(initialOptions);
  const [processing, setProcessing] = useState(false);
  const [tab, setTab] = useState(0);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [domain, setDomain] = useState(''); // 'business' | 'tourism' optional
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [reminders, setReminders] = useState({}); // { [conversationId]: 'YYYY-MM-DD' }

  const [results, setResults] = useState({
    summary: '',
    keyPoints: [],
    actionItems: [], // { description, owner, dueDate }
    followUps: { email: '', linkedin: '', whatsapp: '' },
    translations: {}, // { lang: text }
    sentiment: { tone: 'neutral' },
    intents: [], // [{ type, confidence }]
    entities: [],
    enrichment: { contacts: [] }
  });

  const recognitionRef = useRef(null);
  const isRecognizingRef = useRef(false);
  const wakeLockRef = useRef(null);
  const wasRecordingRef = useRef(false);

  // Load draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.transcript) setTranscript(parsed.transcript);
        if (parsed?.options) setOptions({ ...initialOptions, ...parsed.options });
      }
      const remRaw = localStorage.getItem('touchconnect.conversations.reminders');
      if (remRaw) setReminders(JSON.parse(remRaw));
    } catch {}
  }, []);

  // Autosave
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ transcript, options }));
      } catch {}
    }, 500);
    return () => clearTimeout(id);
  }, [transcript, options]);

  // Load history
  const loadHistory = async (q = '') => {
    try {
      const items = await api.get(`/conversations${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setHistory(items || []);
    } catch (e) {
      console.warn('Failed to load conversations history:', e);
    }
  };

  useEffect(() => {
    if (user) loadHistory();
  }, [user]);

  const supportsSpeech = useMemo(() => {
    return (
      typeof window !== 'undefined' && (
        window.SpeechRecognition || window.webkitSpeechRecognition
      )
    );
  }, []);

  const getRecognizer = () => {
    if (!supportsSpeech) return null;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SR();
    recog.lang = options?.inputLang || 'en-US';
    recog.interimResults = true;
    recog.continuous = true;
    return recog;
  };

  // Prevent duplicated phrases when appending final ASR results
  const dedupeAppend = (existing, addition) => {
    const trimmedAdd = (addition || '').trim();
    if (!trimmedAdd) return existing || '';
    const existingNorm = (existing || '');
    const tail = existingNorm.slice(-200).toLowerCase();
    const addLower = trimmedAdd.toLowerCase();
    if (tail.endsWith(addLower) || tail.includes(addLower)) {
      return existingNorm;
    }
    const sep = existingNorm && !existingNorm.endsWith(' ') ? ' ' : '';
    return (existingNorm + sep + trimmedAdd).trim();
  };

  const startWakeLock = async () => {
    try {
      if ('wakeLock' in navigator && navigator.wakeLock.request) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch {}
  };

  const releaseWakeLock = async () => {
    try {
      await wakeLockRef.current?.release?.();
      wakeLockRef.current = null;
    } catch {}
  };

  const handleStart = async () => {
    if (!supportsSpeech) {
      setState('recording');
      return;
    }
    await startWakeLock();
    const recog = getRecognizer();
    recognitionRef.current = recog;
    isRecognizingRef.current = true;

    recog.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const res = event.results[i];
        if (res.isFinal) {
          setTranscript((prev) => dedupeAppend(prev, res[0].transcript));
          setPartial('');
        } else {
          interim += res[0].transcript;
        }
      }
      setPartial(interim);
    };
    recog.onend = () => {
      if (isRecognizingRef.current) {
        try { recog.start(); } catch {}
      }
    };
    try { recog.start(); } catch {}
    setState('recording');
  };

  const handlePause = () => {
    if (!supportsSpeech) {
      setState('paused');
      return;
    }
    isRecognizingRef.current = false;
    try { recognitionRef.current?.stop(); } catch {}
    setState('paused');
    releaseWakeLock();
  };

  const handleResume = async () => {
    if (!supportsSpeech) {
      setState('recording');
      return;
    }
    await handleStart();
  };

  const handleStop = () => {
    if (supportsSpeech) {
      isRecognizingRef.current = false;
      try { recognitionRef.current?.stop(); } catch {}
    }
    setPartial('');
    setState('reviewing');
    releaseWakeLock();
  };

  const handleProcess = async () => {
    if (!transcript.trim()) return;
    setProcessing(true);
    setState('processing');
    try {
      const request = {
        transcript: transcript.trim(),
        features: {
          summarize: !!options.summarize,
          actions: !!options.extractActions,
          followups: !!options.generateFollowups,
          sentimentIntent: !!options.analyzeSentiment,
          translations: Array.isArray(options.outputLanguages) ? options.outputLanguages : []
        },
        language: {
          autoDetect: !!options.inputAutoDetect,
          inputLang: options.inputLang || null,
          outputLanguages: Array.isArray(options.outputLanguages) ? options.outputLanguages : []
        }
      };
      const res = await AIService.processConversation(request);
      setResults({
        summary: res?.summary || '',
        keyPoints: res?.keyPoints || [],
        actionItems: res?.actionItems || [],
        followUps: res?.followUps || { email: '', linkedin: '', whatsapp: '' },
        translations: res?.translations || {},
        sentiment: res?.sentiment || { tone: 'neutral' },
        intents: res?.intents || [],
        entities: res?.entities || [],
        enrichment: res?.enrichment || { contacts: [] }
      });
      // Auto-title suggestion
      setTitle(prev => prev || autoTitle(res));
      setTab(0);
      setState('results');
    } catch (e) {
      alert('Failed to process conversation. Please try again.');
      setState('reviewing');
    } finally {
      setProcessing(false);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
    } catch {}
  };

  const openMail = (body) => {
    const url = `mailto:?subject=${encodeURIComponent('Follow-up')}&body=${encodeURIComponent(body || '')}`;
    window.open(url, '_blank');
  };

  const openWhatsApp = (text) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text || '')}`;
    window.open(url, '_blank');
  };

  const openLinkedIn = (text) => {
    // LinkedIn share URL (note: messaging is limited; this opens share composer)
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://touchconnect.org')}&summary=${encodeURIComponent(text || '')}`;
    window.open(url, '_blank');
  };

  const disabledProcess = processing || !transcript.trim();

  const autoTitle = (res) => {
    const kp = Array.isArray(res?.keyPoints) && res.keyPoints[0] ? res.keyPoints[0] : '';
    if (kp) return kp.slice(0, 80);
    const first = (transcript || '').split(/\s+/).slice(0, 8).join(' ');
    return first || 'Conversation';
  };

  const saveReminderLocal = (id, dateStr) => {
    const next = { ...reminders, [id]: dateStr };
    setReminders(next);
    try { localStorage.setItem('touchconnect.conversations.reminders', JSON.stringify(next)); } catch {}
  };

  const saveConversation = async () => {
    if (!user) return alert('Please sign in to save.');
    if (!transcript.trim()) return alert('Nothing to save.');
    setSaving(true);
    try {
      const payload = {
        title: (title || autoTitle(results)).trim(),
        transcript: transcript.trim(),
        options_json: JSON.stringify({ options, domain, tags }),
        results_json: JSON.stringify(results),
        tags: (tags || '').trim() || null,
        domain: (domain || '').trim() || null,
        contact_id: selectedContactId || null
      };
      const created = await api.post('/conversations', payload);
      setTitle('');
      setTags('');
      setSelectedContactId('');
      await loadHistory(search);
      // Optional: set reminder default to today+2
    } catch (e) {
      alert(e.message || 'Failed to save conversation');
    } finally {
      setSaving(false);
    }
  };

  const deleteConversation = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await api.delete(`/conversations/${id}`);
      await loadHistory(search);
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const loadConversationIntoView = async (conv) => {
    try {
      setTranscript(conv.transcript || '');
      try {
        const parsedOptions = JSON.parse(conv.options_json || '{}');
        setOptions(prev => ({ ...prev, ...(parsedOptions?.options || {} ) }));
        setDomain(parsedOptions?.domain || '');
        setTags(parsedOptions?.tags || '');
      } catch {}
      try {
        const parsedResults = JSON.parse(conv.results_json || '{}');
        setResults({
          summary: parsedResults?.summary || '',
          keyPoints: parsedResults?.keyPoints || [],
          actionItems: parsedResults?.actionItems || [],
          followUps: parsedResults?.followUps || { email: '', linkedin: '', whatsapp: '' },
          translations: parsedResults?.translations || {},
          sentiment: parsedResults?.sentiment || { tone: 'neutral' },
          intents: parsedResults?.intents || [],
          entities: parsedResults?.entities || [],
          enrichment: parsedResults?.enrichment || { contacts: [] }
        });
        setState('results');
        setTab(0);
      } catch {}
    } catch {}
  };

  const fetchContacts = async () => {
    try {
      const list = await api.get('/contacts');
      setContacts(list || []);
    } catch (e) {
      console.warn('Failed to load contacts', e);
    }
  };

  const openLinkContactDialog = (conversationId) => {
    setSelectedConversationId(conversationId);
    setContactDialogOpen(true);
    fetchContacts();
  };

  const closeLinkContactDialog = () => {
    setContactDialogOpen(false);
    setSelectedConversationId(null);
    setSelectedContactId('');
  };

  const confirmLinkContact = async () => {
    if (!selectedConversationId || !selectedContactId) { closeLinkContactDialog(); return; }
    try {
      await api.request(`/conversations/${selectedConversationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ contact_id: Number(selectedContactId) }),
        headers: { 'Content-Type': 'application/json' }
      });
      await loadHistory(search);
    } catch (e) {
      alert('Failed to link contact');
    } finally {
      closeLinkContactDialog();
    }
  };

  const buildTextRecap = () => {
    const lines = [];
    lines.push(title || autoTitle(results));
    lines.push('');
    if (results.summary) { lines.push('Summary:'); lines.push(results.summary); lines.push(''); }
    if (Array.isArray(results.keyPoints) && results.keyPoints.length) {
      lines.push('Key Points:');
      results.keyPoints.forEach(p => lines.push(`- ${p}`));
      lines.push('');
    }
    if (Array.isArray(results.actionItems) && results.actionItems.length) {
      lines.push('Action Items:');
      results.actionItems.forEach(a => lines.push(`- ${a.description} ${a.owner ? `(Owner: ${a.owner})` : ''} ${a.dueDate ? `(Due: ${a.dueDate})` : ''}`));
      lines.push('');
    }
    return lines.join('\n');
  };

  const buildMarkdownRecap = () => {
    const lines = [];
    lines.push(`# ${title || autoTitle(results)}`);
    lines.push('');
    if (results.summary) { lines.push('## Summary'); lines.push(results.summary); lines.push(''); }
    if (Array.isArray(results.keyPoints) && results.keyPoints.length) {
      lines.push('## Key Points');
      results.keyPoints.forEach(p => lines.push(`- ${p}`));
      lines.push('');
    }
    if (Array.isArray(results.actionItems) && results.actionItems.length) {
      lines.push('## Action Items');
      results.actionItems.forEach(a => lines.push(`- ${a.description}${a.owner ? ` • Owner: ${a.owner}` : ''}${a.dueDate ? ` • Due: ${a.dueDate}` : ''}`));
      lines.push('');
    }
    return lines.join('\n');
  };

  const downloadFile = (name, content, type) => {
    const blob = new Blob([content], { type });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const emailRecap = () => {
    const body = buildTextRecap();
    const url = `mailto:?subject=${encodeURIComponent(title || 'Conversation recap')}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank');
  };

  const toDate = (val) => {
    if (!val) return null;
    // Accept ISO date or relative strings like 'next Tuesday' (skip parsing relative for now)
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDateICS = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
  };

  const downloadICS = () => {
    const now = new Date();
    const lines = [];
    lines.push('BEGIN:VCALENDAR');
    lines.push('VERSION:2.0');
    lines.push('PRODID:-//TouchConnect//Conversations//EN');
    const items = Array.isArray(results.actionItems) ? results.actionItems : [];
    items.forEach((a, idx) => {
      const due = toDate(a?.dueDate) || new Date(now.getTime() + 24*3600*1000);
      lines.push('BEGIN:VEVENT');
      lines.push(`UID:conv-${Date.now()}-${idx}@touchconnect`);
      lines.push(`DTSTAMP:${formatDateICS(now)}`);
      lines.push(`DTSTART:${formatDateICS(due)}`);
      lines.push(`SUMMARY:${(title || 'Conversation')} - ${a?.description || 'Follow-up'}`);
      lines.push(`DESCRIPTION:${a?.description || ''}${a?.owner ? ` (Owner: ${a.owner})` : ''}`);
      lines.push('END:VEVENT');
    });
    lines.push('END:VCALENDAR');
    downloadFile(`${(title || 'conversation').replace(/\s+/g,'_')}.ics`, lines.join('\n'), 'text/calendar');
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fafc, #eef2f7)' }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          Conversations
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Capture voice or typed notes after meetings, then generate summaries, action items, follow-ups, and translations.
        </Typography>

        {/* Controls */}
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1}>
                {(state === 'idle' || state === 'reviewing' || state === 'results') && (
                  <Button variant="contained" startIcon={<MicIcon />} onClick={handleStart}>
                    Start Recording
                  </Button>
                )}
                {state === 'recording' && (
                  <>
                    <Button color="warning" variant="outlined" startIcon={<PauseIcon />} onClick={handlePause}>
                      Pause
                    </Button>
                    <Button color="error" variant="outlined" startIcon={<StopIcon />} onClick={handleStop}>
                      Stop
                    </Button>
                  </>
                )}
                {state === 'paused' && (
                  <>
                    <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={handleResume}>
                      Resume
                    </Button>
                    <Button color="error" variant="outlined" startIcon={<StopIcon />} onClick={handleStop}>
                      Stop
                    </Button>
                  </>
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Copy transcript">
                  <span>
                    <IconButton onClick={() => copyText((transcript + ' ' + partial).trim())} disabled={!transcript && !partial}>
                      <ContentCopyIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            {!supportsSpeech && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Note: Browser speech recognition not available. You can still type or paste notes below.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Transcript + Options */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Transcript
                </Typography>
                <TextField
                  multiline
                  minRows={isMobile ? 8 : 12}
                  fullWidth
                  value={transcript}
                  onChange={(e) => {
                    setTranscript(e.target.value);
                    setPartial('');
                  }}
                  onFocus={() => {
                    // Pause recognition while typing to avoid cursor jumps
                    if (state === 'recording') {
                      wasRecordingRef.current = true;
                    }
                    try { isRecognizingRef.current = false; recognitionRef.current?.stop?.(); } catch {}
                    setState('paused');
                    releaseWakeLock();
                  }}
                  onBlur={() => {
                    // Auto-resume if we had been recording before focusing
                    if (wasRecordingRef.current && supportsSpeech) {
                      wasRecordingRef.current = false;
                      handleResume();
                    }
                  }}
                  placeholder="Speak, type, or paste your meeting notes here..."
                  InputProps={{
                    endAdornment: (
                      <IconButton aria-label="Clear transcript" onClick={() => { setTranscript(''); setPartial(''); }} size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )
                  }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip label={state.toUpperCase()} color={state === 'recording' ? 'success' : state === 'paused' ? 'warning' : 'default'} size="small" />
                  {partial && <Chip label="Listening… (not yet inserted)" size="small" color="info" />}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Options
                </Typography>
                <FormGroup>
                  <FormControlLabel control={<Checkbox checked={options.summarize} onChange={(e) => setOptions((prev) => ({ ...prev, summarize: e.target.checked }))} />} label="Summarize conversation" />
                  <FormControlLabel control={<Checkbox checked={options.extractActions} onChange={(e) => setOptions((prev) => ({ ...prev, extractActions: e.target.checked }))} />} label="Extract action items" />
                  <FormControlLabel control={<Checkbox checked={options.generateFollowups} onChange={(e) => setOptions((prev) => ({ ...prev, generateFollowups: e.target.checked }))} />} label="Generate follow-up drafts" />
                  <FormControlLabel control={<Checkbox checked={options.analyzeSentiment} onChange={(e) => setOptions((prev) => ({ ...prev, analyzeSentiment: e.target.checked }))} />} label="Analyze sentiment & intent" />
                </FormGroup>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Language
                </Typography>
                <FormGroup>
                  <FormControlLabel control={<Checkbox checked={options.inputAutoDetect} onChange={(e) => setOptions((prev) => ({ ...prev, inputAutoDetect: e.target.checked }))} />} label="Auto-detect input language" />
                </FormGroup>
                {!options.inputAutoDetect && (
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel id="input-lang-label">Input Language</InputLabel>
                    <Select
                      labelId="input-lang-label"
                      label="Input Language"
                      value={options.inputLang || 'en-US'}
                      onChange={(e) => setOptions((prev) => ({ ...prev, inputLang: e.target.value }))}
                    >
                      <MenuItem value={'en-US'}>English (US)</MenuItem>
                      <MenuItem value={'en-GB'}>English (UK)</MenuItem>
                      <MenuItem value={'fr-FR'}>French</MenuItem>
                      <MenuItem value={'es-ES'}>Spanish</MenuItem>
                      <MenuItem value={'de-DE'}>German</MenuItem>
                      <MenuItem value={'it-IT'}>Italian</MenuItem>
                      <MenuItem value={'pt-PT'}>Portuguese</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel id="output-lang-label">Translate to</InputLabel>
                  <Select
                    labelId="output-lang-label"
                    label="Translate to"
                    multiple
                    value={options.outputLanguages}
                    onChange={(e) => setOptions((prev) => ({ ...prev, outputLanguages: e.target.value }))}
                    renderValue={(selected) => selected.map((c) => languageOptions.find((o) => o.code === c)?.label || c).join(', ')}
                  >
                    {languageOptions.map((opt) => (
                      <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                  <Button variant="gradient" startIcon={<InsightsIcon />} onClick={handleProcess} disabled={disabledProcess}>
                    {processing ? 'Processing…' : 'Process Conversation'}
                  </Button>
                  <Button variant="outlined" onClick={() => { setTranscript(''); setPartial(''); setState('idle'); }}>
                    Clear
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Processing Spinner */}
        {state === 'processing' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Results */}
        {state === 'results' && (
          <Card sx={{ borderRadius: 3, mt: 3 }}>
            <CardContent>
              {/* Actions Row */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <TextField size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ minWidth: 220 }} />
                  <TextField size="small" label="Tags (comma)" value={tags} onChange={(e) => setTags(e.target.value)} sx={{ minWidth: 180 }} />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="domain-label">Domain</InputLabel>
                    <Select labelId="domain-label" label="Domain" value={domain} onChange={(e) => setDomain(e.target.value)}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="business">Business</MenuItem>
                      <MenuItem value="tourism">Tourism</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="contained" onClick={saveConversation} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                  <Button variant="outlined" onClick={() => downloadFile(`${(title||'conversation').replace(/\s+/g,'_')}.txt`, buildTextRecap(), 'text/plain')}>Export .txt</Button>
                  <Button variant="outlined" onClick={() => downloadFile(`${(title||'conversation').replace(/\s+/g,'_')}.md`, buildMarkdownRecap(), 'text/markdown')}>Export .md</Button>
                  <Button variant="outlined" onClick={emailRecap}>Email recap</Button>
                  <Button variant="outlined" onClick={downloadICS}>Download .ics</Button>
                </Stack>
              </Stack>

              <Tabs value={tab} onChange={(e, v) => setTab(v)} variant={isMobile ? 'scrollable' : 'standard'} allowScrollButtonsMobile>
                <Tab label="Summary" icon={<InsightsIcon />} iconPosition="start" />
                <Tab label="Action Items" icon={<ChecklistIcon />} iconPosition="start" />
                <Tab label="Follow-ups" icon={<EmailIcon />} iconPosition="start" />
                <Tab label="Translations" icon={<TranslateIcon />} iconPosition="start" />
                <Tab label="Entities" icon={<InsightsIcon />} iconPosition="start" />
              </Tabs>
              <Divider sx={{ mb: 2 }} />

              {tab === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Summary</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{results.summary || '—'}</Typography>
                  {!!results.keyPoints?.length && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Key Points</Typography>
                      <Stack spacing={0.75}>
                        {results.keyPoints.map((p, i) => (
                          <Typography key={i} variant="body2">• {p}</Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    {results.sentiment?.tone && <Chip label={`Tone: ${results.sentiment.tone}${typeof results.sentiment.score === 'number' ? ` (${Math.round(results.sentiment.score * 100)}%)` : ''}`} size="small" />} 
                    {Array.isArray(results.intents) && results.intents.slice(0, 4).map((intent, i) => (
                      <Chip
                        key={i}
                        label={`${intent?.type || 'intent'}${typeof intent?.confidence === 'number' ? ` (${Math.round(intent.confidence * 100)}%)` : ''}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {tab === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Action Items</Typography>
                  {(!results.actionItems || results.actionItems.length === 0) && (
                    <Typography variant="body2" color="text.secondary">No action items detected.</Typography>
                  )}
                  <Stack spacing={1.25}>
                    {results.actionItems?.map((a, i) => (
                      <Card key={i} sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="body1" sx={{ mb: 0.5 }}>{a.description || '—'}</Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {a.owner && <Chip label={`Owner: ${a.owner}`} size="small" />}
                            {a.dueDate && <Chip label={`Due: ${a.dueDate}`} size="small" color="warning" />}
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}

              {tab === 2 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Email</Typography>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" onClick={() => copyText(results.followUps?.email)}><ContentCopyIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => openMail(results.followUps?.email)}><SendIcon fontSize="small" /></IconButton>
                          </Stack>
                        </Stack>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{results.followUps?.email || '—'}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>LinkedIn</Typography>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" onClick={() => copyText(results.followUps?.linkedin)}><ContentCopyIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => openLinkedIn(results.followUps?.linkedin)}><LinkedInIcon fontSize="small" /></IconButton>
                          </Stack>
                        </Stack>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{results.followUps?.linkedin || '—'}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>WhatsApp</Typography>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" onClick={() => copyText(results.followUps?.whatsapp)}><ContentCopyIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => openWhatsApp(results.followUps?.whatsapp)}><WhatsAppIcon fontSize="small" /></IconButton>
                          </Stack>
                        </Stack>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{results.followUps?.whatsapp || '—'}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {tab === 3 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Translations</Typography>
                  {(!results.translations || Object.keys(results.translations).length === 0) && (
                    <Typography variant="body2" color="text.secondary">No translations requested.</Typography>
                  )}
                  <Grid container spacing={2}>
                    {Object.entries(results.translations || {}).map(([lang, text]) => (
                      <Grid item xs={12} md={6} key={lang}>
                        <Card sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{languageOptions.find((o) => o.code === lang)?.label || lang}</Typography>
                              <IconButton size="small" onClick={() => copyText(text)}><ContentCopyIcon fontSize="small" /></IconButton>
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text || '—'}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {tab === 4 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Entities & Enrichment</Typography>
                  {(!results.entities || results.entities.length === 0) && (!results.enrichment?.contacts || results.enrichment.contacts.length === 0) && (
                    <Typography variant="body2" color="text.secondary">No entities detected.</Typography>
                  )}
                  {!!results.entities?.length && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Entities</Typography>
                      <Grid container spacing={2}>
                        {results.entities.map((e, i) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Card sx={{ borderRadius: 2 }}>
                              <CardContent>
                                <Typography variant="body1" sx={{ mb: 0.5 }}>{e.text || '—'}</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  {e.type && <Chip label={e.type} size="small" />}
                                  {e.role && <Chip label={`Role: ${e.role}`} size="small" />}
                                  {e.email && <Chip label={e.email} size="small" />}
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  {!!results.enrichment?.contacts?.length && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Suggested Contacts</Typography>
                      <Grid container spacing={2}>
                        {results.enrichment.contacts.map((c, i) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Card sx={{ borderRadius: 2 }}>
                              <CardContent>
                                <Typography variant="body1" sx={{ mb: 0.5 }}>{c.name || '—'}</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                  {c.company && <Chip label={c.company} size="small" />}
                                  {c.email && <Chip label={c.email} size="small" />}
                                  {c.website && <Chip label={c.website} size="small" />}
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <Button size="small" variant="outlined" onClick={() => copyText(JSON.stringify(c))}>Copy</Button>
                                  {/* Optional future: Add to contacts via backend */}
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* History */}
        {user && (
          <Card sx={{ borderRadius: 3, mt: 3 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>History</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField size="small" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
                  <Button variant="outlined" onClick={() => loadHistory(search)}>Search</Button>
                </Stack>
              </Stack>
              {(!history || history.length === 0) && (
                <Typography variant="body2" color="text.secondary">No conversations saved yet.</Typography>
              )}
              <Grid container spacing={2}>
                {history.map((h) => (
                  <Grid item xs={12} md={6} key={h.id}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{h.title || 'Conversation'}</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ my: 1 }}>
                          {h.tags && <Chip label={h.tags} size="small" />}
                          {h.domain && <Chip label={h.domain} size="small" />}
                          {reminders?.[h.id] && <Chip label={`Reminder: ${reminders[h.id]}`} size="small" color="warning" />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{new Date(h.created_at).toLocaleString()}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => loadConversationIntoView(h)}>Load</Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => deleteConversation(h.id)}>Delete</Button>
                          <Button size="small" variant="outlined" onClick={() => openLinkContactDialog(h.id)}>Link contact</Button>
                          <TextField size="small" type="date" value={reminders[h.id] || ''} onChange={(e) => saveReminderLocal(h.id, e.target.value)} />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Link Contact Dialog */}
        <Dialog open={contactDialogOpen} onClose={closeLinkContactDialog} fullWidth maxWidth="sm">
          <DialogTitle>Link to contact</DialogTitle>
          <DialogContent>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="contact-select-label">Contact</InputLabel>
              <Select labelId="contact-select-label" label="Contact" value={selectedContactId} onChange={(e) => setSelectedContactId(e.target.value)}>
                {contacts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name || c.email || `Contact ${c.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLinkContactDialog}>Cancel</Button>
            <Button variant="contained" onClick={confirmLinkContact}>Link</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ConversationsPage;


